/**
 * The licence state (GET /license, pkg/api/license.go) and what the shell says
 * about it (design eval F25). Settings › License and the app-wide banner read
 * the same query (hooks/useLicenseState.ts), so they can never disagree.
 */

export interface LicenseFeature {
  code: string;
  name?: string;
  enabled: boolean;
}

export interface LicenseState {
  mode: 'saas' | 'onprem';
  /** Only on-prem shows the activation form; a SaaS plan follows its subscription. */
  can_activate: boolean;
  status: 'active' | 'expired' | 'invalid' | 'unlicensed' | 'unavailable';
  valid: boolean;
  company?: string;
  plan_name?: string;
  plan_code?: string;
  product_name?: string;
  license_key?: string;
  subscription_id?: string;
  start_date?: string;
  end_date?: string;
  days_remaining: number;
  billing_cycle?: string;
  /** null means unlimited — rendered as "Unlimited", never as 0 or ∞. */
  user_limit: number | null;
  agent_limit: number | null;
  user_count: number;
  agent_count: number;
  features: LicenseFeature[];
  last_checked_at?: string;
  message?: string;
}

/** A licence this close to its end date is called out across the app. */
export const LICENSE_EXPIRING_DAYS = 14;

export type LicenseAlert =
  | { kind: 'suspended'; status: LicenseState['status'] }
  | { kind: 'expiring'; days: number; endDate?: string };

/**
 * What the shell should say about a licence, or null when there is nothing to
 * say. Not valid (expired, invalid, never activated) suspends the product;
 * valid with an end date inside LICENSE_EXPIRING_DAYS is a warning. An
 * "unavailable" licence that is still valid is held on its last known state
 * and is not alarming on its own.
 */
export function licenseAlert(state: LicenseState | null | undefined): LicenseAlert | null {
  if (!state || typeof state !== 'object' || !state.status) return null;
  if (state.valid === false) return { kind: 'suspended', status: state.status };
  const days = Number(state.days_remaining);
  if (state.end_date && Number.isFinite(days) && days >= 0 && days <= LICENSE_EXPIRING_DAYS) {
    return { kind: 'expiring', days, endDate: state.end_date };
  }
  return null;
}
